import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useChats } from '@/hooks/useChats';
import { FloatingTextField } from '@/shared/TextField';
import { TextFieldVariant } from '@/shared/enums';
import { AccountSwitcher } from '../AccountSwitcher';
import ChatList from '@/components/ChatList';
import { SettingsMenu } from '../SettingsMenu';
import { useSheetSelection } from '@/contexts/sheetSelection';
import useSheets from '@/hooks/useSheets';
import { useCategories, useFilteredSheets } from '@/hooks/useCategories';
import { BRAND_LOGO_PATH, BRAND_NAME, appPath } from '@/config/deutscheBankDemo';

interface SidebarProps {
  onBack?: () => void;
  onNavigate?: () => void;
}

export const Sidebar = ({ onBack, onNavigate }: SidebarProps) => {
  const navigate = useNavigate();
  const { accountId, chatId } = useParams<{ accountId?: string; chatId?: string }>();
  const [searchQuery, setSearchQuery] = useState('');
  const { selectedSheetId, selectedCategoryIds } = useSheetSelection();
  const { sheets } = useSheets(accountId);
  const numericAccountId = accountId ? Number.parseInt(accountId, 10) : null;
  const { categories } = useCategories(numericAccountId);
  const { sheets: categoryModeSheets } = useFilteredSheets(numericAccountId, selectedCategoryIds);

  const selectedSheet = useMemo(() => {
    if (!selectedSheetId) return null;
    return sheets.find((s) => s.id === selectedSheetId) ?? null;
  }, [selectedSheetId, sheets]);

  const { chats, loading: chatsLoading, error: chatsError } = useChats(accountId, {
    skipLoad: selectedCategoryIds.length > 0 && !selectedSheetId,
    sheetId: selectedSheetId ?? undefined,
  });

  const filteredChats = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalizedQuery) return chats;

    return chats.filter((chat) => {
      const normalizedTitle = chat.title.toLowerCase().replace(/\s+/g, ' ');
      return normalizedTitle.includes(normalizedQuery);
    });
  }, [chats, searchQuery]);

  const displayName = selectedSheet?.display_name || selectedSheet?.title;
  const isSelectedCompanyMode = Boolean(selectedSheetId);
  const isCategoryMode = selectedCategoryIds.length > 0 && !isSelectedCompanyMode;

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const group of categories?.groups ?? []) {
      for (const category of group.categories) {
        map.set(category.id, category.name);
      }
    }
    return map;
  }, [categories]);

  const navigateTo = (path: string) => {
    navigate(path);
    onNavigate?.();
  };

  return (
    <>
      {/* Deutsche Bank Header */}
      <div className="h-16 md:h-[92px] flex items-center px-4 border-b border-peach-1 shrink-0">
        {!isSelectedCompanyMode && !isCategoryMode && onBack && (
          <button
            onClick={() => {
              onBack();
              onNavigate?.();
            }}
            className="mr-3 w-8 h-8 flex items-center justify-center rounded-md hover:bg-peach transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13 8H3M7 4L3 8l4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <img
          src={BRAND_LOGO_PATH}
          alt={BRAND_NAME}
          className="w-[108px] h-12 md:w-[134px] md:h-[60px] object-contain"
        />
      </div>

      {(isSelectedCompanyMode || isCategoryMode) && onBack && (
        <div className="px-4 pt-6 shrink-0">
          <button
            onClick={() => {
              onBack();
              onNavigate?.();
            }}
            className="w-full h-[44px] bg-peach rounded-lg px-4 flex items-center justify-center gap-2 text-md text-grey-black tracking-[0.3px] hover:bg-peach-1 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M13 8H3M7 4L3 8l4 4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Back to models</span>
          </button>
        </div>
      )}

      {/* Company info */}
      {selectedSheet && (
        <div className="px-4 pt-6 shrink-0">
          <div className="h-[149px] flex items-center justify-center overflow-hidden">
            {selectedSheet.logo_url ? (
              <img
                src={selectedSheet.logo_url}
                alt={displayName}
                className="max-h-[134px] max-w-full object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : null}
            <div
              className={`w-full h-full bg-peach rounded-md flex items-center justify-center ${selectedSheet.logo_url ? 'hidden' : ''}`}
            >
              <span className="text-[32px] font-bold text-pif-green">
                {displayName?.charAt(0)}
              </span>
            </div>
          </div>
          <p className="mt-6 text-[19px] font-bold text-grey-black tracking-[0.3px] leading-tight">
            {displayName}
          </p>
          {selectedSheet.description && (
            <p className="mt-2 text-sm text-grey-hover line-clamp-3">
              {selectedSheet.description}
            </p>
          )}
        </div>
      )}

      {isCategoryMode && (
        <div className="px-4 py-4 flex-1 min-h-0 flex flex-col">
          <div className="bg-peach rounded-2xl py-2 flex flex-col min-h-0 max-h-full overflow-hidden">
            <div className="h-[44px] px-4 flex items-center border-b border-peach-1 shrink-0">
              <p className="text-sm font-bold text-grey-black tracking-[0.3px]">
                Chat about {categoryModeSheets.length} compan
                {categoryModeSheets.length === 1 ? 'y' : 'ies'}
              </p>
            </div>
            <div className="pt-1 overflow-auto">
              {categoryModeSheets.map((sheet) => {
                const subtitle = selectedCategoryIds.length === 1
                  ? categoryNameById.get(selectedCategoryIds[0]) || 'Selected sector'
                  : 'Selected sectors';
                return (
                  <div key={sheet.id} className="px-4 py-2">
                    <p className="text-sm text-grey-black tracking-[0.3px] leading-tight">
                      {sheet.display_name || sheet.title}
                    </p>
                    <p className="text-xs text-grey-hover tracking-[0.3px] leading-tight mt-0.5">
                      {subtitle}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!isSelectedCompanyMode && !isCategoryMode && (
        <div className="flex gap-3 px-4 pt-4 pb-3">
          <SettingsMenu />
          <AccountSwitcher />
        </div>
      )}

      {/* Search */}
      {!isSelectedCompanyMode && !isCategoryMode && (
        <div className="px-4">
          <FloatingTextField
            placeholder="Search in Chats"
            variant={TextFieldVariant.SEARCH}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      )}

      {/* Chats List */}
      {!isCategoryMode && (
        <div className="flex-1 overflow-auto">
          {accountId && (
            <ChatList
              title="Chats"
              items={filteredChats.map((c) => ({ id: c.id, title: c.title }))}
              loading={chatsLoading}
              error={chatsError ? 'Failed to load chats' : null}
              selectedId={chatId ?? null}
              onSelect={(id) => navigateTo(appPath(`/account/${accountId}/chat/${id}`))}
              onCreate={() => {
                navigateTo(appPath(`/account/${accountId}/chat/new?sheetId=${selectedSheetId}`));
              }}
              emptyMessage={searchQuery.trim() ? `No chats found for "${searchQuery}"` : 'No chats yet'}
            />
          )}
        </div>
      )}
    </>
  )
}
